import {
  MAP_HEIGHT,
  MAP_WIDTH,
  TANK_DIRECTIONS,
  TILE_SIZE,
  Terrain,
  parseWinboloMap,
  writeWinboloMap,
  type MapBase,
  type MapPillbox,
  type MapStart,
} from "@webolo/shared";
import { api, ApiError } from "../api.js";
import { el } from "./dom.js";
import { buildTileAtlas } from "../game/tileArt.js";

// Map builder.
//
// Hash route #builder. Same access gate as the admin screen (the server's
// requireAdmin enforces it on every endpoint we hit). Renders a 256x256
// terrain canvas with a paint palette, plus tools to place spawn points,
// bases, and pillboxes. Saves via the same /api/admin/maps endpoint the
// admin panel uses; new files appear in the admin map list immediately.

interface BuilderState {
  // Terrain grid, row-major [y * MAP_WIDTH + x].
  terrain: Uint8Array;
  pillboxes: MapPillbox[];
  bases: MapBase[];
  starts: MapStart[];
  // Currently selected paint tool.
  tool: Tool;
  // Edge length (in tiles) of the square painted around the cursor for
  // terrain and erase strokes. 1 = single tile, max BRUSH_MAX. Entity
  // tools ignore this and always place a single entity at the cursor.
  brushSize: number;
  // Shape draw mode. "none" = free paint (existing brush behavior).
  // Any other value swaps pointer drag into a "drag to draw a shape"
  // gesture that commits on release.
  shape: ShapeKind;
  // When drawing a rect/square shape: true = fill the area, false =
  // outline only (1-tile-wide border). Line ignores this since it's
  // already 1 tile wide.
  shapeFill: boolean;
  // Facing applied to spawn points placed by the "start" tool, as a
  // compass step (0 = N, rotates CW; see TANK_DIRECTIONS). Clicking an
  // existing spawn re-stamps it with this facing, so the same control
  // both places and rotates spawns.
  startDir: number;
  // The map's display name. Empty until the user types one (or opens an
  // existing map). Saved as the on-disk filename `<name>.map`.
  name: string;
  // If we opened an existing map, remember its id so re-saving with the
  // same name overwrites in-place rather than 409-ing.
  openedId: string | null;
}

type Tool =
  | { kind: "terrain"; terrain: Terrain }
  | { kind: "erase" }
  | { kind: "start" }
  | { kind: "base" }
  | { kind: "pillbox" }
  | { kind: "erase-entity" };

type ShapeKind = "none" | "line" | "rect" | "square" | "circle";

const BRUSH_MAX = 16;

// Spawn-facing choices for the "start" tool, as compass steps into the
// game's 16-direction encoding (0 = N, rotates CW). We expose the eight
// cardinal/intercardinal headings — enough for level design without
// making the picker a 16-entry list.
const SPAWN_FACINGS: { label: string; dir: number }[] = [
  { label: "N",  dir: 0 },
  { label: "NE", dir: 2 },
  { label: "E",  dir: 4 },
  { label: "SE", dir: 6 },
  { label: "S",  dir: 8 },
  { label: "SW", dir: 10 },
  { label: "W",  dir: 12 },
  { label: "NW", dir: 14 },
];

// Palette layout — order shown in the sidebar. We intentionally hide
// BoatOrBuilding (an internal state, not a paintable terrain) and the
// Pillbox/Base tiles (those are placed via entity tools).
const TERRAIN_PALETTE: { terrain: Terrain; label: string }[] = [
  { terrain: Terrain.DeepSea,       label: "Deep Sea" },
  { terrain: Terrain.River,         label: "River" },
  { terrain: Terrain.Boat,          label: "Boat" },
  { terrain: Terrain.Swamp,         label: "Swamp" },
  { terrain: Terrain.Crater,        label: "Crater" },
  { terrain: Terrain.Rubble,        label: "Rubble" },
  { terrain: Terrain.Grass,         label: "Grass" },
  { terrain: Terrain.Forest,        label: "Forest" },
  { terrain: Terrain.Road,          label: "Road" },
  { terrain: Terrain.Wall,          label: "Wall" },
  { terrain: Terrain.HalfBuiltWall, label: "Half Wall" },
];

const ENTITY_TOOLS: { kind: Tool["kind"]; label: string }[] = [
  { kind: "start",         label: "Spawn Point" },
  { kind: "base",          label: "Base" },
  { kind: "pillbox",       label: "Pillbox" },
  { kind: "erase-entity",  label: "Erase Entity" },
];

// Pixel scale of the editor canvas. The full world is 256x256 tiles at
// TILE_SIZE pixels, which is too large to fit on screen — we draw the
// atlas-source tiles at SCALE px each. The user pans by scrolling the
// wrapper element; this keeps the canvas trivially click-pickable.
const SCALE = 6; // 256 * 6 = 1536px wide

export function builderScreen(onExit: () => void): HTMLElement {
  const state: BuilderState = {
    terrain: emptyWaterWorld(),
    pillboxes: [],
    bases: [],
    starts: [],
    tool: { kind: "terrain", terrain: Terrain.Grass },
    brushSize: 1,
    shape: "none",
    shapeFill: true,
    startDir: 0,
    name: "",
    openedId: null,
  };

  const atlas = buildTileAtlas();
  const canvas = el("canvas", {
    class: "builder-canvas",
    width: MAP_WIDTH * SCALE,
    height: MAP_HEIGHT * SCALE,
  }) as HTMLCanvasElement;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("builder canvas ctx unavailable");
  // Pixel-perfect blits: don't smooth the atlas when scaling.
  ctx.imageSmoothingEnabled = false;

  const nameInput = el("input", {
    type: "text",
    class: "admin-input builder-name",
    placeholder: "Map name",
    maxlength: 64,
  }) as HTMLInputElement;
  nameInput.addEventListener("input", () => { state.name = nameInput.value; });

  const status = el("div", { class: "admin-status" });
  const setStatus = (text: string, kind: "ok" | "err" | null = null): void => {
    status.textContent = text;
    status.className = "admin-status" + (kind === "ok" ? " admin-status-ok" : kind === "err" ? " admin-status-err" : "");
  };

  // While dragging a shape, the pointermove handler renders a preview
  // overlay on top of the committed map. The preview is non-mutating:
  // only on pointerup do we apply the shape to state.
  let shapeDrag: { startX: number; startY: number; endX: number; endY: number; override?: Tool } | null = null;
  // Crosshair guides — tracked on every pointermove (including hover
  // without a button held). null when the cursor is off the canvas.
  let hoverCell: { x: number; y: number } | null = null;
  const repaint = (): void => {
    drawAll(ctx, atlas, state);
    if (shapeDrag) {
      drawShapePreview(ctx, atlas, state, shapeDrag.startX, shapeDrag.startY, shapeDrag.endX, shapeDrag.endY, shapeDrag.override);
    }
    if (hoverCell) drawCrosshair(ctx, hoverCell.x, hoverCell.y);
  };

  // Paint dragging — track the last painted cell so a drag stroke
  // doesn't redo the same brush stamp dozens of times per frame.
  let painting = false;
  let lastPaintKey = -1;
  const cellAt = (clientX: number, clientY: number): { x: number; y: number } | null => {
    const r = canvas.getBoundingClientRect();
    const sx = canvas.width / r.width;   // device px per CSS px
    const sy = canvas.height / r.height;
    const tx = Math.floor(((clientX - r.left) * sx) / SCALE);
    const ty = Math.floor(((clientY - r.top)  * sy) / SCALE);
    if (tx < 0 || tx >= MAP_WIDTH || ty < 0 || ty >= MAP_HEIGHT) return null;
    return { x: tx, y: ty };
  };
  const paintAt = (clientX: number, clientY: number, override?: Tool): void => {
    const cell = cellAt(clientX, clientY);
    if (!cell) return;
    const key = cell.y * MAP_WIDTH + cell.x;
    if (key === lastPaintKey) return;
    lastPaintKey = key;
    applyToolBrush(state, cell.x, cell.y, override);
    repaint();
  };

  canvas.addEventListener("pointerdown", (e) => {
    if (e.button !== 0) return;
    canvas.setPointerCapture(e.pointerId);
    const cell = cellAt(e.clientX, e.clientY);
    if (state.shape !== "none") {
      // Shape mode: start the drag, draw a preview on subsequent moves,
      // commit on release. Free-paint state stays untouched.
      if (!cell) return;
      shapeDrag = { startX: cell.x, startY: cell.y, endX: cell.x, endY: cell.y };
      repaint();
      return;
    }
    painting = true;
    lastPaintKey = -1;
    paintAt(e.clientX, e.clientY);
  });
  canvas.addEventListener("pointermove", (e) => {
    const cell = cellAt(e.clientX, e.clientY);
    // Update the hover crosshair on every move so the guides track the
    // cursor even when no button is held.
    const sameHover = hoverCell && cell && hoverCell.x === cell.x && hoverCell.y === cell.y;
    if (shapeDrag) {
      if (!cell) return;
      const sameShape = cell.x === shapeDrag.endX && cell.y === shapeDrag.endY;
      if (sameShape && sameHover) return;
      shapeDrag.endX = cell.x;
      shapeDrag.endY = cell.y;
      hoverCell = cell;
      repaint();
      return;
    }
    if (painting) {
      hoverCell = cell;
      paintAt(e.clientX, e.clientY);
      return;
    }
    if (sameHover) return;
    hoverCell = cell;
    repaint();
  });
  canvas.addEventListener("pointerleave", () => {
    if (!hoverCell) return;
    hoverCell = null;
    repaint();
  });
  const endPaint = (e: PointerEvent): void => {
    if (shapeDrag) {
      // Commit the shape using the active tool (or whichever override
      // was set by, e.g., a right-click-to-erase shape gesture).
      const tool = shapeDrag.override ?? state.tool;
      forEachShapeCell(state.shape, state.shapeFill, shapeDrag.startX, shapeDrag.startY, shapeDrag.endX, shapeDrag.endY, (x, y) => {
        applyTool(state, x, y, tool);
      });
      shapeDrag = null;
      if (canvas.hasPointerCapture(e.pointerId)) canvas.releasePointerCapture(e.pointerId);
      repaint();
      return;
    }
    if (!painting) return;
    painting = false;
    if (canvas.hasPointerCapture(e.pointerId)) canvas.releasePointerCapture(e.pointerId);
  };
  canvas.addEventListener("pointerup", endPaint);
  canvas.addEventListener("pointercancel", endPaint);

  // Right-click erases (DeepSea + clears entities). Honours shape mode
  // too — so right-drag with the Rectangle shape clears a filled/outlined
  // rectangle back to water.
  canvas.addEventListener("contextmenu", (e) => e.preventDefault());
  canvas.addEventListener("pointerdown", (e) => {
    if (e.button !== 2) return;
    canvas.setPointerCapture(e.pointerId);
    const cell = cellAt(e.clientX, e.clientY);
    if (!cell) return;
    if (state.shape !== "none") {
      shapeDrag = { startX: cell.x, startY: cell.y, endX: cell.x, endY: cell.y, override: { kind: "erase" } };
      repaint();
      return;
    }
    applyToolBrush(state, cell.x, cell.y, { kind: "erase" });
    repaint();
  });

  // Brush size slider — controls the side length of the square stamp
  // applied around the cursor for terrain/erase strokes. Entity tools
  // ignore the slider and always place a single entity.
  const brushSlider = el("input", {
    type: "range",
    class: "builder-brush",
    min: 1,
    max: BRUSH_MAX,
    step: 1,
    value: state.brushSize,
  }) as HTMLInputElement;
  const brushReadout = el("span", { class: "builder-brush-readout" }, `${state.brushSize}×${state.brushSize}`);
  brushSlider.addEventListener("input", () => {
    const v = Math.max(1, Math.min(BRUSH_MAX, Number(brushSlider.value) | 0));
    state.brushSize = v;
    brushReadout.textContent = `${v}×${v}`;
  });

  // Spawn-facing picker — sets the direction baked into spawn points
  // placed with the Spawn Point tool. Clicking an existing spawn with
  // that tool re-stamps it to this facing, so this doubles as a rotate
  // control for already-placed spawns.
  const facingSelect = el("select", { class: "admin-input builder-facing" }) as HTMLSelectElement;
  for (const f of SPAWN_FACINGS) {
    facingSelect.append(el("option", { value: String(f.dir) }, f.label));
  }
  facingSelect.value = String(state.startDir);
  facingSelect.addEventListener("change", () => {
    state.startDir = Number(facingSelect.value) % TANK_DIRECTIONS;
  });

  // Dedicated Erase button — picks the erase tool, which paints
  // DeepSea + clears entities under the brush. Keeps the keyboard /
  // right-click shortcut for users who prefer it but gives a clear
  // primary control for MacBook trackpads.
  const eraseBtn = el("button", { type: "button", class: "admin-btn builder-erase-btn" }, "ERASE");
  const syncEraseBtn = (): void => {
    eraseBtn.classList.toggle("active", state.tool.kind === "erase");
  };
  const { node: palette, refresh: refreshPalette } = buildPalette(state, syncEraseBtn);
  eraseBtn.addEventListener("click", () => {
    state.tool = { kind: "erase" };
    refreshPalette();
    syncEraseBtn();
  });
  syncEraseBtn();

  const saveBtn = el("button", { type: "button", class: "admin-btn admin-btn-primary" }, "SAVE MAP");
  saveBtn.addEventListener("click", async () => {
    const name = state.name.trim();
    if (!name) {
      setStatus("Give the map a name first.", "err");
      nameInput.focus();
      return;
    }
    if (!/^[A-Za-z0-9 _.\-()]+$/.test(name)) {
      setStatus("Name may only use letters, numbers, spaces, and _ - . ( )", "err");
      return;
    }
    if (state.starts.length === 0) {
      setStatus("Place at least one spawn point before saving.", "err");
      return;
    }
    saveBtn.setAttribute("disabled", "");
    setStatus("Saving…");
    try {
      const bytes = writeWinboloMap({
        terrain: state.terrain,
        pillboxes: state.pillboxes,
        bases: state.bases,
        starts: state.starts,
      });
      const dataBase64 = bytesToBase64(bytes);
      const slug = slugify(name);
      const overwrite = state.openedId !== null && state.openedId === slug;
      await api.adminUploadMap({
        filename: `${name}.map`,
        dataBase64,
        overwrite,
      });
      state.openedId = slug;
      setStatus(overwrite ? "Saved (overwritten)." : "Saved. Map is now in the admin list.", "ok");
    } catch (e) {
      setStatus(uploadErr(e), "err");
    } finally {
      saveBtn.removeAttribute("disabled");
    }
  });

  const newBtn = el("button", { type: "button", class: "admin-btn" }, "NEW WATER WORLD");
  newBtn.addEventListener("click", () => {
    if (!confirm("Discard the current map and start over?")) return;
    state.terrain = emptyWaterWorld();
    state.pillboxes = [];
    state.bases = [];
    state.starts = [];
    state.openedId = null;
    state.name = "";
    nameInput.value = "";
    setStatus("Cleared.", null);
    repaint();
  });

  const openSelect = el("select", { class: "admin-input builder-open" }) as HTMLSelectElement;
  openSelect.append(el("option", { value: "" }, "Open existing map…"));
  const openBtn = el("button", { type: "button", class: "admin-btn" }, "OPEN");
  void refreshOpenList(openSelect);
  openBtn.addEventListener("click", async () => {
    const id = openSelect.value;
    if (!id) {
      setStatus("Pick a map to open.", "err");
      return;
    }
    openBtn.setAttribute("disabled", "");
    setStatus("Loading…");
    try {
      const { dataBase64 } = await api.adminGetMapRaw(id);
      const bytes = base64ToBytes(dataBase64);
      // Use the slug-derived display name from the option text so
      // "My Map" round-trips through the open dialog as-is.
      const displayName = openSelect.options[openSelect.selectedIndex]?.textContent?.trim() ?? id;
      const parsed = parseWinboloMap(bytes, displayName);
      // Strip Pillbox/Base overlays back to grass so the painter can
      // edit the underlying terrain without smearing pillbox/base
      // sprites around the canvas as the user paints.
      const t = new Uint8Array(parsed.terrain);
      for (let i = 0; i < t.length; i++) {
        if (t[i] === Terrain.Pillbox || t[i] === Terrain.Base) t[i] = Terrain.Grass;
      }
      state.terrain = t;
      state.pillboxes = parsed.pillboxes.map((p) => ({ ...p }));
      state.bases     = parsed.bases.map((b) => ({ ...b }));
      state.starts    = parsed.starts.map((s) => ({ ...s }));
      state.name = displayName;
      state.openedId = id;
      nameInput.value = displayName;
      setStatus(`Loaded "${displayName}".`, "ok");
      repaint();
    } catch (e) {
      setStatus(uploadErr(e), "err");
    } finally {
      openBtn.removeAttribute("disabled");
    }
  });

  const wrap = el(
    "div",
    { class: "screen builder" },
    el(
      "div",
      { class: "builder-shell" },
      el(
        "header",
        { class: "admin-head" },
        el("div", { class: "admin-logo" },
          el("span", { class: "logo-we" }, "WE"),
          el("span", { class: "logo-bolo" }, "BOLO"),
          el("span", { class: "admin-tag" }, "BUILDER"),
        ),
        el("button", { class: "admin-back", type: "button", onclick: onExit }, "← BACK TO LOBBY"),
      ),
      el(
        "div",
        { class: "builder-toolbar" },
        nameInput,
        saveBtn,
        newBtn,
        openSelect,
        openBtn,
        el("div", { class: "builder-brush-wrap" },
          el("span", { class: "builder-brush-lbl" }, "BRUSH"),
          brushSlider,
          brushReadout,
        ),
        el("div", { class: "builder-facing-wrap" },
          el("span", { class: "builder-brush-lbl" }, "SPAWN FACING"),
          facingSelect,
        ),
        eraseBtn,
        status,
      ),
      el(
        "div",
        { class: "builder-main" },
        palette,
        el("div", { class: "builder-canvas-wrap" }, canvas),
      ),
      el("div", { class: "builder-help" },
        "Left-click paints; click + drag draws strokes. The BRUSH slider " +
        "sets how many tiles the stroke covers (entity tools always place " +
        "a single entity). With the Spawn Point tool, SPAWN FACING sets " +
        "which way tanks face when they spawn — click an existing spawn to " +
        "rotate it to the selected heading. The ERASE button (or " +
        "right-click) clears to deep sea. Pick a SHAPE on the left to drag " +
        "a line, rectangle, or square instead — the FILL / LINE toggle next " +
        "to the section title controls whether rect / square are filled or " +
        "outlined.",
      ),
    ),
  );

  repaint();
  return wrap;
}

function buildPalette(
  state: BuilderState,
  onChange: () => void,
): { node: HTMLElement; refresh: () => void } {
  const node = el("div", { class: "builder-palette" });
  const renderActive = (): void => {
    for (const c of Array.from(node.querySelectorAll(".builder-palette-row")) as HTMLElement[]) {
      const key = c.getAttribute("data-key");
      if (!key) continue;
      if (key.startsWith("shape:")) {
        c.classList.toggle("active", key === `shape:${state.shape}`);
      } else {
        c.classList.toggle("active", key === toolKey(state.tool));
      }
    }
  };
  const afterPick = (): void => {
    renderActive();
    onChange();
  };
  node.append(el("div", { class: "builder-palette-h" }, "TERRAIN"));
  for (const p of TERRAIN_PALETTE) {
    const swatch = swatchEl(p.terrain);
    const btn = el(
      "button",
      { type: "button", class: "builder-palette-row", "data-key": `terrain:${p.terrain}` },
      swatch,
      el("span", { class: "builder-palette-lbl" }, p.label),
    );
    btn.addEventListener("click", () => {
      state.tool = { kind: "terrain", terrain: p.terrain };
      afterPick();
    });
    node.append(btn);
  }
  node.append(el("div", { class: "builder-palette-h" }, "ENTITIES"));
  for (const e of ENTITY_TOOLS) {
    const swatch = entitySwatchEl(e.kind);
    const btn = el(
      "button",
      { type: "button", class: "builder-palette-row", "data-key": e.kind },
      swatch,
      el("span", { class: "builder-palette-lbl" }, e.label),
    );
    btn.addEventListener("click", () => {
      if (e.kind === "start")   state.tool = { kind: "start" };
      else if (e.kind === "base")    state.tool = { kind: "base" };
      else if (e.kind === "pillbox") state.tool = { kind: "pillbox" };
      else                            state.tool = { kind: "erase-entity" };
      afterPick();
    });
    node.append(btn);
  }

  // Shapes section. Header carries the FILL / LINE toggle that flips
  // state.shapeFill (only matters for rect / square; line is always
  // 1-tile wide). Clicking the active shape again deselects it,
  // returning to free-paint mode.
  const fillToggle = el(
    "button",
    { type: "button", class: "builder-shape-toggle" },
    state.shapeFill ? "FILL" : "LINE",
  );
  fillToggle.addEventListener("click", () => {
    state.shapeFill = !state.shapeFill;
    fillToggle.textContent = state.shapeFill ? "FILL" : "LINE";
  });
  node.append(el("div", { class: "builder-palette-h builder-palette-h-row" },
    el("span", {}, "SHAPES"),
    fillToggle,
  ));
  const SHAPES: { kind: Exclude<ShapeKind, "none">; label: string }[] = [
    { kind: "line",   label: "Line" },
    { kind: "rect",   label: "Rectangle" },
    { kind: "square", label: "Square" },
    { kind: "circle", label: "Circle" },
  ];
  for (const s of SHAPES) {
    const swatch = shapeSwatchEl(s.kind);
    const btn = el(
      "button",
      { type: "button", class: "builder-palette-row", "data-key": `shape:${s.kind}` },
      swatch,
      el("span", { class: "builder-palette-lbl" }, s.label),
    );
    btn.addEventListener("click", () => {
      // Toggle-off if user clicks the already-active shape, so they
      // can leave shape mode without having to click "Free paint".
      state.shape = state.shape === s.kind ? "none" : s.kind;
      renderActive();
    });
    node.append(btn);
  }

  renderActive();
  return { node, refresh: renderActive };
}

function toolKey(t: Tool): string {
  if (t.kind === "terrain") return `terrain:${t.terrain}`;
  return t.kind;
}

function swatchEl(t: Terrain): HTMLElement {
  // 24x24 sample drawn from the same procedural tile art the game uses
  // so the palette looks like what the user will actually paint.
  const c = el("canvas", { class: "builder-swatch", width: 24, height: 24 }) as HTMLCanvasElement;
  const cctx = c.getContext("2d");
  if (cctx) {
    const atlas = builderAtlasCache();
    cctx.imageSmoothingEnabled = false;
    cctx.drawImage(atlas, t * TILE_SIZE, 0, TILE_SIZE, TILE_SIZE, 0, 0, 24, 24);
  }
  return c;
}

function entitySwatchEl(kind: Tool["kind"]): HTMLElement {
  const c = el("canvas", { class: "builder-swatch", width: 24, height: 24 }) as HTMLCanvasElement;
  const cctx = c.getContext("2d");
  if (!cctx) return c;
  cctx.imageSmoothingEnabled = false;
  // Grass background so entity icons look like they sit on a tile.
  const atlas = builderAtlasCache();
  cctx.drawImage(atlas, Terrain.Grass * TILE_SIZE, 0, TILE_SIZE, TILE_SIZE, 0, 0, 24, 24);
  if (kind === "start") {
    cctx.fillStyle = "#ffd000";
    cctx.beginPath();
    cctx.moveTo(12, 4);
    cctx.lineTo(20, 18);
    cctx.lineTo(12, 14);
    cctx.lineTo(4, 18);
    cctx.closePath();
    cctx.fill();
  } else if (kind === "base") {
    cctx.drawImage(atlas, Terrain.Base * TILE_SIZE, 0, TILE_SIZE, TILE_SIZE, 0, 0, 24, 24);
  } else if (kind === "pillbox") {
    cctx.drawImage(atlas, Terrain.Pillbox * TILE_SIZE, 0, TILE_SIZE, TILE_SIZE, 0, 0, 24, 24);
  } else {
    cctx.strokeStyle = "#ff6b6b";
    cctx.lineWidth = 2;
    cctx.beginPath();
    cctx.moveTo(5, 5); cctx.lineTo(19, 19);
    cctx.moveTo(19, 5); cctx.lineTo(5, 19);
    cctx.stroke();
  }
  return c;
}

function shapeSwatchEl(kind: Exclude<ShapeKind, "none">): HTMLElement {
  // 24×24 swatch showing the shape outline. Visualised on a dark
  // background so the same icon reads regardless of which terrain
  // colour the user has selected.
  const c = el("canvas", { class: "builder-swatch builder-swatch-dark", width: 24, height: 24 }) as HTMLCanvasElement;
  const cctx = c.getContext("2d");
  if (!cctx) return c;
  cctx.strokeStyle = "#ffd000";
  cctx.lineWidth = 2;
  cctx.beginPath();
  if (kind === "line") {
    cctx.moveTo(4, 20);
    cctx.lineTo(20, 4);
    cctx.stroke();
  } else if (kind === "rect") {
    cctx.strokeRect(4, 7, 16, 10);
  } else if (kind === "square") {
    cctx.strokeRect(5, 5, 14, 14);
  } else {
    cctx.arc(12, 12, 7, 0, Math.PI * 2);
    cctx.stroke();
  }
  return c;
}

let _atlas: HTMLCanvasElement | null = null;
function builderAtlasCache(): HTMLCanvasElement {
  if (!_atlas) _atlas = buildTileAtlas();
  return _atlas;
}

// Rasterises a shape between (x0, y0) and (x1, y1) and invokes cb for
// each cell. Line uses Bresenham; rect/square use min/max bounds with
// optional outline-only mode. Coordinates are clamped to the map.
function forEachShapeCell(
  shape: ShapeKind,
  fill: boolean,
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  cb: (x: number, y: number) => void,
): void {
  if (shape === "none") return;
  if (shape === "line") {
    bresenham(x0, y0, x1, y1, cb);
    return;
  }
  if (shape === "circle") {
    // Centre = the drag start; radius = chebyshev-rounded distance to
    // the cursor. This lets the user click the middle and drag out the
    // radius, which feels more natural than a bounding-box circle.
    const dx = x1 - x0;
    const dy = y1 - y0;
    const r = Math.round(Math.sqrt(dx * dx + dy * dy));
    if (fill) {
      const r2 = r * r;
      const yLo = Math.max(0, y0 - r);
      const yHi = Math.min(MAP_HEIGHT - 1, y0 + r);
      for (let y = yLo; y <= yHi; y++) {
        const dyc = y - y0;
        const span = Math.floor(Math.sqrt(r2 - dyc * dyc));
        const xLo = Math.max(0, x0 - span);
        const xHi = Math.min(MAP_WIDTH - 1, x0 + span);
        for (let x = xLo; x <= xHi; x++) cb(x, y);
      }
    } else {
      midpointCircle(x0, y0, r, cb);
    }
    return;
  }
  // Rect and Square share an axis-aligned bounding box. Square pins
  // the bbox to a square rooted at the start so the user feels they're
  // dragging a square outward from where they clicked.
  let lx: number, ly: number, hx: number, hy: number;
  if (shape === "square") {
    const side = Math.max(Math.abs(x1 - x0), Math.abs(y1 - y0));
    const dx = x1 >= x0 ? side : -side;
    const dy = y1 >= y0 ? side : -side;
    lx = Math.min(x0, x0 + dx);
    hx = Math.max(x0, x0 + dx);
    ly = Math.min(y0, y0 + dy);
    hy = Math.max(y0, y0 + dy);
  } else {
    lx = Math.min(x0, x1);
    hx = Math.max(x0, x1);
    ly = Math.min(y0, y1);
    hy = Math.max(y0, y1);
  }
  lx = Math.max(0, lx); ly = Math.max(0, ly);
  hx = Math.min(MAP_WIDTH - 1, hx); hy = Math.min(MAP_HEIGHT - 1, hy);
  if (fill) {
    for (let y = ly; y <= hy; y++) {
      for (let x = lx; x <= hx; x++) cb(x, y);
    }
  } else {
    // Outline: top + bottom rows, then left + right columns excluding
    // the corners already covered.
    for (let x = lx; x <= hx; x++) {
      cb(x, ly);
      if (hy !== ly) cb(x, hy);
    }
    for (let y = ly + 1; y < hy; y++) {
      cb(lx, y);
      if (hx !== lx) cb(hx, y);
    }
  }
}

// Bresenham's line algorithm — cells between (x0, y0) and (x1, y1),
// inclusive. Standard integer form; clamps each plotted cell to the
// map so off-grid drags don't corrupt memory.
function bresenham(x0: number, y0: number, x1: number, y1: number, cb: (x: number, y: number) => void): void {
  let x = x0;
  let y = y0;
  const dx = Math.abs(x1 - x0);
  const dy = -Math.abs(y1 - y0);
  const sx = x0 < x1 ? 1 : -1;
  const sy = y0 < y1 ? 1 : -1;
  let err = dx + dy;
  while (true) {
    if (x >= 0 && x < MAP_WIDTH && y >= 0 && y < MAP_HEIGHT) cb(x, y);
    if (x === x1 && y === y1) break;
    const e2 = 2 * err;
    if (e2 >= dy) { err += dy; x += sx; }
    if (e2 <= dx) { err += dx; y += sy; }
  }
}

// Midpoint circle algorithm — plots the 1-tile-wide ring of radius r
// around (cx, cy). Each octant is mirrored to the other seven; cells
// are clamped to the map.
function midpointCircle(cx: number, cy: number, r: number, cb: (x: number, y: number) => void): void {
  if (r <= 0) {
    if (cx >= 0 && cx < MAP_WIDTH && cy >= 0 && cy < MAP_HEIGHT) cb(cx, cy);
    return;
  }
  let x = r;
  let y = 0;
  let err = 1 - r;
  const plot = (px: number, py: number): void => {
    if (px >= 0 && px < MAP_WIDTH && py >= 0 && py < MAP_HEIGHT) cb(px, py);
  };
  while (x >= y) {
    plot(cx + x, cy + y);
    plot(cx - x, cy + y);
    plot(cx + x, cy - y);
    plot(cx - x, cy - y);
    plot(cx + y, cy + x);
    plot(cx - y, cy + x);
    plot(cx + y, cy - x);
    plot(cx - y, cy - x);
    y++;
    if (err <= 0) {
      err += 2 * y + 1;
    } else {
      x--;
      err += 2 * (y - x) + 1;
    }
  }
}

// Dotted crosshair through the cursor cell — vertical line at the
// cell's centre column, horizontal at its centre row, with the cell
// itself outlined. Helps the user line up shapes with existing
// features. Drawn after the shape preview so it stays on top.
function drawCrosshair(ctx: CanvasRenderingContext2D, x: number, y: number): void {
  const cx = x * SCALE + SCALE / 2;
  const cy = y * SCALE + SCALE / 2;
  ctx.save();
  ctx.strokeStyle = "rgba(255, 208, 0, 0.55)";
  ctx.lineWidth = 1;
  ctx.setLineDash([3, 3]);
  ctx.beginPath();
  ctx.moveTo(cx + 0.5, 0);
  ctx.lineTo(cx + 0.5, MAP_HEIGHT * SCALE);
  ctx.moveTo(0, cy + 0.5);
  ctx.lineTo(MAP_WIDTH * SCALE, cy + 0.5);
  ctx.stroke();
  // Solid outline on the focused cell so the snap point is obvious.
  ctx.setLineDash([]);
  ctx.strokeStyle = "rgba(255, 208, 0, 0.9)";
  ctx.strokeRect(x * SCALE + 0.5, y * SCALE + 0.5, SCALE - 1, SCALE - 1);
  ctx.restore();
}

// Renders a preview of the in-flight shape on top of the committed
// map. Uses the active asset's atlas tile for terrain shapes and a
// yellow ring for entity shapes (since the entity sprite would be
// confusing at a glance for, e.g., 100 pillboxes lined up). The
// erase override draws a deep-sea tint so right-drag previews clearly.
function drawShapePreview(
  ctx: CanvasRenderingContext2D,
  atlas: HTMLCanvasElement,
  state: BuilderState,
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  override: Tool | undefined,
): void {
  const tool = override ?? state.tool;
  ctx.save();
  ctx.globalAlpha = 0.7;
  forEachShapeCell(state.shape, state.shapeFill, x0, y0, x1, y1, (x, y) => {
    if (tool.kind === "terrain") {
      ctx.drawImage(
        atlas, tool.terrain * TILE_SIZE, 0, TILE_SIZE, TILE_SIZE,
        x * SCALE, y * SCALE, SCALE, SCALE,
      );
    } else if (tool.kind === "erase") {
      ctx.drawImage(
        atlas, Terrain.DeepSea * TILE_SIZE, 0, TILE_SIZE, TILE_SIZE,
        x * SCALE, y * SCALE, SCALE, SCALE,
      );
    } else {
      // Entity / erase-entity preview: a yellow tile-sized outline.
      ctx.strokeStyle = "#ffd000";
      ctx.lineWidth = 1;
      ctx.strokeRect(x * SCALE + 0.5, y * SCALE + 0.5, SCALE - 1, SCALE - 1);
    }
  });
  ctx.restore();
}

// Applies the active tool over a brush-shaped square centred on
// (x, y). Terrain and erase tools cover state.brushSize tiles per
// side; entity tools deliberately ignore the brush so a stray slider
// nudge doesn't drop 16 bases on one click. `override` lets the
// right-click handler use the erase tool without mutating state.tool.
function applyToolBrush(
  state: BuilderState,
  x: number,
  y: number,
  override?: Tool,
): void {
  const tool = override ?? state.tool;
  if (tool.kind === "terrain" || tool.kind === "erase") {
    const half = Math.floor(state.brushSize / 2);
    const x0 = Math.max(0, x - half);
    const y0 = Math.max(0, y - half);
    const x1 = Math.min(MAP_WIDTH,  x - half + state.brushSize);
    const y1 = Math.min(MAP_HEIGHT, y - half + state.brushSize);
    const t = tool.kind === "terrain" ? tool.terrain : Terrain.DeepSea;
    for (let yy = y0; yy < y1; yy++) {
      for (let xx = x0; xx < x1; xx++) {
        state.terrain[yy * MAP_WIDTH + xx] = t;
        removeEntityAt(state, xx, yy);
      }
    }
    return;
  }
  applyTool(state, x, y, tool);
}

function applyTool(state: BuilderState, x: number, y: number, toolOverride?: Tool): void {
  const tool = toolOverride ?? state.tool;
  switch (tool.kind) {
    case "terrain":
      state.terrain[y * MAP_WIDTH + x] = tool.terrain;
      // Painting terrain on top of an entity drops the entity so the
      // two stay in sync.
      removeEntityAt(state, x, y);
      break;
    case "erase":
      state.terrain[y * MAP_WIDTH + x] = Terrain.DeepSea;
      removeEntityAt(state, x, y);
      break;
    case "start": {
      // Re-stamping an existing spawn just rotates it to the currently
      // selected facing, so the same tool both places and rotates.
      const existing = state.starts.find((s) => s.x === x && s.y === y);
      if (existing) {
        existing.direction = state.startDir;
      } else {
        if (state.starts.length >= 16) return;
        state.starts.push({ x, y, direction: state.startDir });
      }
      break;
    }
    case "base":
      if (!state.bases.some((b) => b.x === x && b.y === y)) {
        if (state.bases.length >= 16) return;
        state.bases.push({ x, y, owner: 0, armor: 90, shells: 40, mines: 40 });
      }
      break;
    case "pillbox":
      if (!state.pillboxes.some((p) => p.x === x && p.y === y)) {
        if (state.pillboxes.length >= 16) return;
        state.pillboxes.push({ x, y, owner: 0, armor: 15, speed: 50 });
      }
      break;
    case "erase-entity":
      removeEntityAt(state, x, y);
      break;
  }
}

function removeEntityAt(state: BuilderState, x: number, y: number): void {
  state.pillboxes = state.pillboxes.filter((p) => !(p.x === x && p.y === y));
  state.bases     = state.bases.filter((b) => !(b.x === x && b.y === y));
  state.starts    = state.starts.filter((s) => !(s.x === x && s.y === y));
}

function emptyWaterWorld(): Uint8Array {
  const t = new Uint8Array(MAP_WIDTH * MAP_HEIGHT);
  t.fill(Terrain.DeepSea);
  return t;
}

function drawAll(
  ctx: CanvasRenderingContext2D,
  atlas: HTMLCanvasElement,
  state: BuilderState,
): void {
  // Background: clear to the deep-sea atlas tile so unpainted areas
  // look identical to the in-game ocean.
  ctx.imageSmoothingEnabled = false;
  for (let y = 0; y < MAP_HEIGHT; y++) {
    for (let x = 0; x < MAP_WIDTH; x++) {
      const t = state.terrain[y * MAP_WIDTH + x];
      ctx.drawImage(
        atlas,
        t * TILE_SIZE, 0, TILE_SIZE, TILE_SIZE,
        x * SCALE, y * SCALE, SCALE, SCALE,
      );
    }
  }
  // Entity overlays — drawn after terrain so an entity tile still
  // shows up clearly even when the user repaints around it.
  for (const b of state.bases) {
    ctx.drawImage(
      atlas, Terrain.Base * TILE_SIZE, 0, TILE_SIZE, TILE_SIZE,
      b.x * SCALE, b.y * SCALE, SCALE, SCALE,
    );
  }
  for (const p of state.pillboxes) {
    ctx.drawImage(
      atlas, Terrain.Pillbox * TILE_SIZE, 0, TILE_SIZE, TILE_SIZE,
      p.x * SCALE, p.y * SCALE, SCALE, SCALE,
    );
  }
  // Spawn points: a yellow arrow marker rotated to the spawn's facing
  // (0 = N/up, rotating CW). The atlas has no spawn art since gameplay-
  // wise spawn tiles stay as their underlying terrain.
  ctx.fillStyle = "#ffd000";
  for (const s of state.starts) {
    const cx = s.x * SCALE + SCALE / 2;
    const cy = s.y * SCALE + SCALE / 2;
    const r = SCALE * 0.45;
    const angle = ((s.direction % TANK_DIRECTIONS) / TANK_DIRECTIONS) * Math.PI * 2;
    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate(angle);
    ctx.beginPath();
    ctx.moveTo(0, -r);
    ctx.lineTo(r, r * 0.7);
    ctx.lineTo(0, r * 0.3);
    ctx.lineTo(-r, r * 0.7);
    ctx.closePath();
    ctx.fill();
    ctx.restore();
  }
}

async function refreshOpenList(select: HTMLSelectElement): Promise<void> {
  try {
    const { maps } = await api.adminListMaps();
    // Clear options after the placeholder.
    while (select.options.length > 1) select.remove(1);
    for (const m of maps) {
      select.append(el("option", { value: m.id }, m.name));
    }
  } catch {
    // Leave the placeholder — the user will get an explicit error when
    // they try to open one.
  }
}

function slugify(name: string): string {
  // Mirrors slugifyMapName on the server so we can detect an existing-
  // map collision before hitting the network.
  return name
    .replace(/\s+/g, "-")
    .replace(/[^A-Za-z0-9_.-]/g, "")
    .toLowerCase();
}

function bytesToBase64(bytes: Uint8Array): string {
  // Encode in chunks to avoid the apply() arg-length limit on big buffers.
  let s = "";
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    s += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(s);
}

function base64ToBytes(b64: string): Uint8Array {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

function uploadErr(e: unknown): string {
  if (e instanceof ApiError) {
    const code = typeof e.body === "object" && e.body && "error" in e.body
      ? String((e.body as { error: unknown }).error)
      : "";
    switch (code) {
      case "map_exists":      return "A map with that name already exists — open it first if you want to overwrite.";
      case "invalid_map":     return "Generated map didn't parse cleanly. (Bug — please report.)";
      case "invalid_input":   return "Invalid input.";
      case "invalid_size":    return "Map is empty or too large.";
      case "not_authenticated": return "Session expired — log in again.";
      case "forbidden":       return "Admin access only.";
      case "not_found":       return "Map not found.";
      default: return code || "Save failed.";
    }
  }
  return "Save failed.";
}

